import React, { useState } from "react";
import {
  FiImage,
  FiUpload,
  FiX,
  FiPlus,
  FiTrash2,
  FiArrowUp,
  FiArrowDown,
  FiArrowLeft,
  FiRotateCcw,
  FiDroplet,
  FiType,
  FiSmartphone,
  FiFileText,
  FiClock,
} from "react-icons/fi";
import {
  COLOR_GROUPS,
  TEXT_FIELDS,
  RULES_MAX,
  RULE_MAX_LENGTH,
  CARD_IMAGE_ACCEPT,
  CARD_IMAGE_HINT,
  isHexColor,
  resolveTheme,
  rgbOf,
  opacityOf,
  withOpacity,
  toFlutterColor,
  fromFlutterColor,
} from "../../../utils/eventTheme";
import { imageFromClipboard } from "../../../utils/sponsorLogo";

/**
 * The "App Design" step: how the Live Event card and screen look in the app.
 *
 * Everything here is optional. A blank field is not "empty" in the app — it is
 * the app's own default, which is why every input shows that default as its
 * placeholder and the preview renders it.
 *
 * State lives in EventForm, which saves it with the event; the card image file
 * is uploaded after the event is saved, the same way sponsor logos are.
 */

/** Checkerboard behind a swatch, so transparency is visible. */
const CHECKER = {
  backgroundImage: "repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%)",
  backgroundSize: "8px 8px",
};

/**
 * One colour. `fallback` is what the app gets while this is blank — usually the
 * spec default, but some follow another field (see resolveTheme).
 */
const ColorField = ({ def, value, fallback, onChange }) => {
  const raw = String(value ?? "");
  const effective = isHexColor(raw) ? raw.trim().toUpperCase() : fallback;
  const invalid = raw.trim() !== "" && !isHexColor(raw);
  const opacity = opacityOf(effective);

  return (
    <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-200">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="text-[11px] font-semibold text-gray-700 truncate" htmlFor={`theme-${def.field}`}>
          {def.label}
        </label>
        {raw.trim() ? (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[10px] font-semibold text-gray-400 hover:text-blue-600 flex items-center gap-1 transition"
            title="Use the app's default"
          >
            <FiRotateCcw size={10} /> Default
          </button>
        ) : (
          <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 bg-white border border-gray-200 rounded px-1.5 py-0.5">
            App default
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="relative w-8 h-8 shrink-0 rounded-lg border border-gray-300 overflow-hidden" style={CHECKER}>
          <span className="absolute inset-0" style={{ background: effective }} />
          {/* The native picker knows nothing of alpha, so it sets the colour
              and the slider beside it keeps the transparency. */}
          <input
            type="color"
            aria-label={`${def.label} picker`}
            value={rgbOf(effective).toLowerCase()}
            onChange={(e) => onChange(withOpacity(e.target.value, opacity))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </span>
        <input
          id={`theme-${def.field}`}
          type="text"
          value={raw}
          placeholder={fallback}
          maxLength={10}
          // A Flutter code pasted from the app side (0xAARRGGBB) is taken as is.
          onChange={(e) => onChange(fromFlutterColor(e.target.value) || e.target.value)}
          className={`w-[92px] px-2 py-1.5 bg-white border rounded-lg text-[11px] font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
            invalid ? "border-rose-400 text-rose-700" : "border-gray-200 text-gray-800"
          }`}
        />
        <input
          type="range"
          min={0}
          max={100}
          value={opacity}
          aria-label={`${def.label} opacity`}
          onChange={(e) => onChange(withOpacity(effective, e.target.value))}
          className="flex-1 min-w-0 accent-blue-600"
          title="Opacity"
        />
        <span className="w-8 text-right text-[10px] font-semibold text-gray-500 tabular-nums">{opacity}%</span>
      </div>

      {invalid ? (
        <p className="text-[10px] text-rose-600 mt-1">
          Use #RRGGBB, #RRGGBBAA for transparency, or an app code like 0xFF636AE8
        </p>
      ) : (
        <p className="text-[10px] text-gray-400 mt-1">
          {/* The exact value the app receives — alpha first, as Flutter reads it. */}
          <span className="font-mono text-gray-500" title="Sent to the app as Color(0xAARRGGBB)">
            App: {toFlutterColor(effective)}
          </span>
          {def.hint && ` · ${def.hint}`}
        </p>
      )}
    </div>
  );
};

/** One heading per block of the step. */
const BlockTitle = ({ icon: Icon, title, hint }) => (
  <div>
    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
      <Icon size={12} className="text-blue-600" /> {title}
    </p>
    {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

/**
 * An approximation of the app's Live Event card and screen.
 *
 * Not pixel-exact — the app draws it — but close enough to catch white text on
 * a white button, or a title lost in a busy image, before users do.
 */
const ThemePreview = ({ theme, imageUrl, eventName, enabled = true }) => {
  const [comingSoon, setComingSoon] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);

  // Exactly what the API will send the app, blanks filled in.
  const resolved = resolveTheme(theme, eventName || "Event name");
  const color = (field) => resolved[field];
  const text = (field) => resolved[field];

  // An image wins in image mode; otherwise the gradient (start = end for a
  // one-colour card).
  const showImage = theme.card_bg_type === "image" && Boolean(imageUrl);
  const background = `linear-gradient(135deg, ${color("card_gradient_start")}, ${color("card_gradient_end")})`;
  const shadow = color("card_shadow_color");

  const toggle = (on, setOn, labels) => (
    <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
      {labels.map((label, i) => {
        const active = (i === 1) === on;
        return (
          <button
            key={label}
            type="button"
            onClick={() => setOn(i === 1)}
            className={`px-2 py-1 rounded-md text-[10px] font-semibold transition ${
              active ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
          <FiSmartphone size={12} className="text-blue-600" /> Preview
          {!enabled && (
            <span className="normal-case tracking-normal font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
              Off — not shown in the app
            </span>
          )}
        </p>
        <div className="flex items-center gap-1.5">
          {toggle(comingSoon, setComingSoon, ["Live", "Coming soon"])}
          {!comingSoon && toggle(checkedIn, setCheckedIn, ["Check in", "Checked in"])}
        </div>
      </div>

      <div
        className="rounded-[28px] border-[6px] border-gray-800 overflow-hidden shadow-lg"
        style={{ background: color("screen_bg_color") }}
        data-testid="theme-preview"
      >
        {/* App bar */}
        <div className="flex items-center gap-2 px-3 py-2.5" style={{ color: color("app_bar_title_color") }}>
          <FiArrowLeft size={14} />
          <span className="flex-1 text-[13px] font-bold truncate">{text("app_bar_title")}</span>
          <FiClock size={13} />
        </div>

        <div className="px-3 pb-4 space-y-3">
          {/* The card */}
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              background: showImage ? undefined : background,
              boxShadow: `0 8px 18px ${shadow}`,
            }}
          >
            {showImage && (
              <>
                <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                {/* The app darkens an image so the text stays readable. */}
                <div className="absolute inset-0 bg-black/35" />
              </>
            )}

            <div className="relative p-3.5 space-y-2">
              <span
                className="inline-block text-[9px] font-bold uppercase tracking-wider"
                style={{ color: color("card_accent_color") }}
              >
                {comingSoon ? "Coming soon · starts in 2d 14h" : "Live · Day 3 of 15"}
              </span>
              <p className="text-[15px] font-extrabold leading-tight" style={{ color: color("card_title_color") }}>
                {text("card_title")}
              </p>
              <p
                className="text-[10.5px] leading-snug whitespace-pre-line"
                style={{ color: color("card_subtitle_color") }}
              >
                {comingSoon ? text("coming_soon_subtitle") : text("card_subtitle")}
              </p>

              {!comingSoon && (
                <>
                  <div className="flex items-end justify-between gap-2 pt-1">
                    <div>
                      <p className="text-[9.5px]" style={{ color: color("card_subtitle_color") }}>
                        {text("points_label")}
                      </p>
                      <p className="text-[20px] font-extrabold leading-none" style={{ color: color("card_points_color") }}>
                        145.5
                      </p>
                    </div>
                    <span
                      className="px-2 py-1 rounded-full text-[9.5px] font-bold"
                      style={{
                        background: color("card_rank_badge_bg_color"),
                        color: color("card_rank_badge_text_color"),
                      }}
                    >
                      Current Rank #84
                    </span>
                  </div>

                  <div className="h-1.5 rounded-full bg-white/25 overflow-hidden">
                    <div className="h-full w-1/5 rounded-full" style={{ background: color("card_accent_color") }} />
                  </div>
                  <p className="text-[9.5px] font-semibold" style={{ color: color("card_accent_color") }}>
                    2.5 pts to next rank
                  </p>

                  <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-[11px] font-bold" style={{ color: color("card_title_color") }}>
                      {text("check_in_title")}
                    </span>
                    <div className="text-center">
                      <span
                        className="inline-block px-3 py-1.5 rounded-full text-[10.5px] font-bold"
                        style={
                          checkedIn
                            ? {
                                background: color("checked_in_btn_bg_color"),
                                color: color("checked_in_btn_text_color"),
                              }
                            : {
                                background: color("check_in_btn_bg_color"),
                                color: color("check_in_btn_text_color"),
                              }
                        }
                      >
                        {checkedIn ? text("checked_in_button_text") : text("check_in_button_text")}
                      </span>
                      {!checkedIn && (
                        <p className="text-[8.5px] mt-0.5" style={{ color: color("card_subtitle_color") }}>
                          {text("check_in_ad_text")}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* The sections below the card, as their titles and primary colour */}
          {["breakdown_title", "activities_title", "sponsor_title", "rules_title"].map((field) => (
            <div key={field} className="flex items-center gap-2 px-2.5 py-2 rounded-xl border border-black/5 bg-black/[0.03]">
              <span className="w-1 h-3.5 rounded-full" style={{ background: color("primary_color") }} />
              <span className="flex-1 text-[10.5px] font-bold text-gray-800 truncate">{text(field)}</span>
              {field === "sponsor_title" && (
                <span
                  className="px-2 py-0.5 rounded-full border text-[9px] font-bold"
                  style={{ borderColor: color("primary_color"), color: color("primary_color") }}
                >
                  {text("sponsor_btn_text")}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      <p className="text-[10px] text-gray-400">Approximate — the app draws the real card.</p>
    </div>
  );
};

const EventThemeStep = ({
  enabled = false,
  onEnabledChange,
  theme,
  onThemeChange,
  rules,
  onRulesChange,
  defaultRules = [],
  eventName,
  cardImageFile,
  cardImagePreview,
  onPickCardImage,
  onClearCardImage,
}) => {
  const setField = (field) => (value) => onThemeChange((prev) => ({ ...prev, [field]: value }));
  // What each blank colour will be sent as — some follow other fields.
  const resolved = resolveTheme(theme, eventName);
  const isImageMode = theme.card_bg_type === "image";
  const imageUrl = cardImagePreview || String(theme.card_bg_image || "").trim();

  const handlePaste = (event) => {
    const image = imageFromClipboard(event);
    if (!image) return;
    event.preventDefault();
    onPickCardImage(image);
  };

  // ── Terms & Conditions rows ──
  const updateRule = (index, text) => onRulesChange((prev) => prev.map((row, i) => (i === index ? { ...row, text } : row)));
  const removeRule = (index) => onRulesChange((prev) => prev.filter((_, i) => i !== index));
  const moveRule = (index, delta) =>
    onRulesChange((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  const addRule = () => onRulesChange((prev) => [...prev, { _localId: `new-${Date.now()}`, text: "" }]);

  return (
    <div className="space-y-5">
      {/* The switch. Off, the app is sent none of this and keeps its standard
          design; the design below is still saved, ready for when it is on. */}
      <label
        className={`flex items-center justify-between gap-3 p-4 rounded-2xl border cursor-pointer transition ${
          enabled ? "bg-blue-50/60 border-blue-200" : "bg-gray-50 border-gray-200"
        }`}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-bold text-gray-800">Use this design in the app</span>
          <span className="block text-[11px] text-gray-500 mt-0.5">
            {enabled
              ? "On — the app shows the card, colors, text and terms below."
              : "Off — the app shows its standard Live Event design. Everything below is still saved, so you can prepare it and switch on when it is ready."}
          </span>
        </span>
        <span className="relative flex-shrink-0">
          <input
            type="checkbox"
            role="switch"
            aria-label="Use this design in the app"
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            className="peer sr-only"
          />
          <span className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-blue-600 transition-colors" />
          <span className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
        </span>
      </label>

      <div
        className={`grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start transition-opacity ${
          enabled ? "" : "opacity-60"
        }`}
        data-testid="theme-editor"
      >
        <div className="space-y-6 min-w-0">
          {/* ── Background mode ── */}
          <div className="space-y-3">
            <BlockTitle
              icon={FiImage}
              title="Live Event card background"
              hint="A color gradient, or an image of your own. If the image cannot load, the app shows the gradient."
            />

            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Card background">
              {[
                { value: "color", label: "Color / Gradient", hint: "Gradient with star decorations" },
                { value: "image", label: "Image", hint: "Your image, darkened slightly for legibility" },
              ].map((mode) => {
                const active = theme.card_bg_type === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setField("card_bg_type")(mode.value)}
                    className={`p-3 rounded-2xl border text-left transition ${
                      active ? "border-blue-500 bg-blue-50/70 ring-2 ring-blue-500/10" : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span className={`block text-xs font-bold ${active ? "text-blue-800" : "text-gray-700"}`}>{mode.label}</span>
                    <span className="block text-[10.5px] text-gray-500 mt-0.5">{mode.hint}</span>
                  </button>
                );
              })}
            </div>

            {isImageMode && (
              <div
                tabIndex={0}
                onPaste={handlePaste}
                className="p-3 bg-gray-50 rounded-2xl border border-gray-200 space-y-2.5 outline-none focus-within:ring-2 focus-within:ring-blue-500/20"
              >
                <div className="flex items-start gap-3">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="Card background"
                      className="w-40 aspect-[2/1] shrink-0 object-cover rounded-xl border border-gray-200 bg-white"
                      onError={(e) => {
                        e.target.style.visibility = "hidden";
                      }}
                    />
                  ) : (
                    <div className="w-40 aspect-[2/1] shrink-0 rounded-xl border-2 border-dashed border-gray-200 bg-white flex items-center justify-center text-gray-300">
                      <FiImage size={20} />
                    </div>
                  )}

                  <div className="flex-1 min-w-0 space-y-1.5">
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:border-blue-400 hover:text-blue-600 rounded-xl text-xs font-semibold text-gray-600 cursor-pointer transition max-w-full">
                      <FiUpload size={13} />
                      <span className="truncate">{cardImageFile ? cardImageFile.name : "Upload image"}</span>
                      <input
                        type="file"
                        accept={CARD_IMAGE_ACCEPT}
                        className="hidden"
                        data-testid="card-image-input"
                        onChange={(e) => onPickCardImage(e.target.files[0], e.target)}
                      />
                    </label>

                    {!cardImageFile && (
                      <input
                        type="url"
                        placeholder="…or paste an image URL (https://)"
                        value={theme.card_bg_image || ""}
                        onChange={(e) => setField("card_bg_image")(e.target.value)}
                        onPaste={handlePaste}
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-[11px] text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                      />
                    )}

                    {imageUrl && (
                      <button
                        type="button"
                        onClick={onClearCardImage}
                        className="text-[10.5px] font-semibold text-gray-400 hover:text-rose-600 flex items-center gap-1 transition"
                      >
                        <FiX size={11} /> {cardImageFile ? "Remove file" : "Remove image"}
                      </button>
                    )}
                    <p className="text-[10px] text-gray-400">
                      {CARD_IMAGE_HINT}. You can also copy an image and press Ctrl+V here.
                      {cardImageFile && " Uploaded when you save."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Colors ── */}
          <div className="space-y-4">
            <BlockTitle
              icon={FiDroplet}
              title="Colors"
              hint="Leave a color on its app default unless you need it changed. The slider sets transparency."
            />
            {COLOR_GROUPS.map((group) => (
              <div key={group.key} className="space-y-2">
                <p className="text-[11px] font-semibold text-gray-700">
                  {group.title}
                  {group.hint && <span className="font-normal text-gray-400"> · {group.hint}</span>}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {group.fields.map((def) => (
                    <ColorField
                      key={def.field}
                      def={def}
                      value={theme[def.field]}
                      fallback={resolved[def.field]}
                      onChange={setField(def.field)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* ── Text ── */}
          <div className="space-y-3">
            <BlockTitle icon={FiType} title="Text" hint="Blank uses the app's own wording, shown in grey." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TEXT_FIELDS.map((def) => {
                const value = theme[def.field] || "";
                const props = {
                  id: `theme-${def.field}`,
                  value,
                  maxLength: def.max,
                  placeholder: def.default || def.hint || "",
                  onChange: (e) => setField(def.field)(e.target.value),
                  className:
                    "w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20",
                };
                return (
                  <div key={def.field} className={def.multiline ? "sm:col-span-2" : ""}>
                    <label htmlFor={props.id} className="flex items-center justify-between text-xs font-medium text-gray-600 mb-1">
                      <span>{def.label}</span>
                      {value.length > def.max * 0.8 && (
                        <span className="text-[10px] text-gray-400 tabular-nums">
                          {value.length}/{def.max}
                        </span>
                      )}
                    </label>
                    {def.multiline ? <textarea rows={2} {...props} /> : <input type="text" {...props} />}
                    {def.hint && def.default && <p className="text-[10px] text-gray-400 mt-1">{def.hint}</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Terms & Conditions ── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <BlockTitle
                icon={FiFileText}
                title={`Terms & Conditions (${rules.length})`}
                hint="One bullet per row, shown in this order"
              />
              <button
                type="button"
                onClick={addRule}
                disabled={rules.length >= RULES_MAX}
                className="px-3.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-xl transition border border-blue-200 flex items-center gap-1.5 disabled:opacity-40 shrink-0"
              >
                <FiPlus size={14} /> Add rule
              </button>
            </div>

            {rules.length === 0 ? (
              <div className="p-4 border-2 border-dashed border-gray-200 rounded-2xl space-y-2.5" data-testid="default-rules">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-gray-500">
                    None written — the app is sent these standard rules:
                  </p>
                  {defaultRules.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        onRulesChange(defaultRules.map((text, i) => ({ _localId: `default-${i}-${Date.now()}`, text })))
                      }
                      className="px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 text-[11px] font-semibold rounded-xl transition border border-gray-200 shrink-0"
                    >
                      Customize these
                    </button>
                  )}
                </div>
                <ol className="list-decimal pl-5 space-y-1 text-[11px] text-gray-400">
                  {defaultRules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ol>
              </div>
            ) : (
              rules.map((row, idx) => (
                <div key={row._localId || idx} className="p-2.5 bg-gray-50 rounded-2xl border border-gray-200 flex items-start gap-2">
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveRule(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-30"
                      title="Move up"
                    >
                      <FiArrowUp size={12} />
                    </button>
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold">
                      {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => moveRule(idx, 1)}
                      disabled={idx === rules.length - 1}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-30"
                      title="Move down"
                    >
                      <FiArrowDown size={12} />
                    </button>
                  </div>
                  <textarea
                    rows={2}
                    maxLength={RULE_MAX_LENGTH}
                    placeholder="e.g. Fair play enforced: automated bots will be disqualified."
                    value={row.text}
                    onChange={(e) => updateRule(idx, e.target.value)}
                    className="flex-1 min-w-0 px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => removeRule(idx)}
                    className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition"
                    title="Remove"
                  >
                    <FiTrash2 size={15} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="lg:sticky lg:top-4">
          <ThemePreview theme={theme} imageUrl={imageUrl} eventName={eventName} enabled={enabled} />
        </div>
      </div>
    </div>
  );
};

export default EventThemeStep;
